import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Type, QrCode, Upload, ShieldCheck, CheckCircle2, Eye,
  Shuffle, Loader2, Trash2, Lock, Unlock, GitBranch, Link2, Copy,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PostcardCanvas } from "@/components/admin/PostcardCanvas";
import { AuthImage, openAuthedPdf } from "@/components/admin/AuthImage";

const card = "rounded-2xl bg-white border border-slate-200 p-4";
const label = "text-[10px] font-bold uppercase tracking-wider text-slate-500";
const input = "mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-xs";

const uid = () => `el-${Math.random().toString(36).slice(2, 10)}`;

export default function PostcardStudioEditor() {
  const { vid } = useParams();
  const [spec, setSpec] = useState(null);
  const [version, setVersion] = useState(null);
  const [template, setTemplate] = useState(null);
  const [locked, setLocked] = useState(false);
  const [side, setSide] = useState("front");
  const [front, setFront] = useState([]);
  const [back, setBack] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [artworkKey, setArtworkKey] = useState(0);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignMeta, setCampaignMeta] = useState(null);
  const [campaignsError, setCampaignsError] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [recipientsState, setRecipientsState] = useState("idle");
  const [inviteId, setInviteId] = useState("");
  const [previewMeta, setPreviewMeta] = useState(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [previewError, setPreviewError] = useState("");
  const [proofs, setProofs] = useState(null);
  const [proofsError, setProofsError] = useState("");
  const [assigned, setAssigned] = useState([]);
  const frontFile = useRef(null);
  const backFile = useRef(null);

  const elements = side === "front" ? front : back;
  const setElements = side === "front" ? setFront : setBack;

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/postcard-studio/versions/${vid}`);
    setVersion(data.version);
    setTemplate(data.template);
    setLocked(data.locked);
    setFront(data.version.front_elements || []);
    setBack(data.version.reverse_elements || []);
    setPreflight(data.version.preflight_status || null);
    setAssigned(data.assigned_campaigns || []);
    setDirty(false);
  }, [vid]);

  const loadCampaigns = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/postcard-studio/campaigns");
      setCampaignMeta(data);
      setCampaigns(data.campaigns || []);
      setCampaignsError("");
      return data;
    } catch (e) {
      setCampaignsError(e.response?.data?.detail || "Could not load postcard campaigns");
      return null;
    }
  }, []);

  const convertToPostcard = async (c) => {
    setBusy(true);
    try {
      await api.patch(`/admin/property-campaigns/${c.id}`, { mail_format: "postcard" });
      toast.success(`${c.name} is now a postcard campaign`);
      const data = await loadCampaigns();
      const match = (data?.campaigns || []).find((x) => x.id === c.id);
      if (match) setCampaignId(match.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change the mail format");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    api.get("/admin/postcard-studio/spec").then(({ data }) => setSpec(data)).catch(() => {});
    loadCampaigns().then((data) => {
      if (data?.campaigns?.length && !campaignId) setCampaignId(data.campaigns[0].id);
    });
    load().catch((e) => toast.error(e.response?.data?.detail || "Could not load template version"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vid]);

  useEffect(() => {
    setInviteId("");
    setRecipients([]);
    setPreviewMeta(null);
    setPreviewError("");
    setProofs(null);
    if (!campaignId) { setRecipientsState("idle"); return; }
    setRecipientsState("loading");
    api.get(`/admin/postcard-studio/versions/${vid}/recipients`, { params: { campaign_id: campaignId } })
      .then(({ data }) => {
        setRecipients(data.recipients || []);
        setInviteId(data.recipients?.[0]?.id || "");
        setRecipientsState("loaded");
      })
      .catch((e) => {
        setRecipients([]);
        setRecipientsState(e.response?.data?.detail || "Could not load campaign recipients");
      });
  }, [campaignId, vid]);

  const sel = useMemo(() => elements.find((e) => e.id === selected) || null, [elements, selected]);

  const updateEl = useCallback((next, opts) => {
    setElements((prev) => prev.map((e) => (e.id === next.id ? next : e)));
    if (!opts?.transient) setDirty(true);
  }, [setElements]);

  /* Copy the QR to the other side: size, source and lock come across,
     but the position is reset to a safe default for that canvas so it
     can never land in the Bing postal strip. */
  const copyQrToOtherSide = () => {
    const other = side === "front" ? "back" : "front";
    const target = other === "front" ? front : back;
    if (target.some((e) => e.kind === "qr")) {
      toast.error("This side already has a dynamic QR code.");
      return;
    }
    const base = qrDefaults(other, spec);
    const copy = { ...base, id: uid(), w_mm: sel.w_mm, h_mm: sel.h_mm,
                   locked: !!sel.locked };
    (other === "front" ? setFront : setBack)((p) => [...p, copy]);
    setDirty(true);
    setSide(other);
    setSelected(copy.id);
    toast.success(`QR copied to the ${other} — position reset to a safe spot, `
                  + "please review before saving");
  };

  const addText = () => {
    const el = {
      id: uid(), kind: "text", x_mm: side === "front" ? 12 : 6, y_mm: 20,
      w_mm: side === "front" ? 150 : 104, h_mm: 30,
      text: "DO YOU KNOW WHAT’S ACTUALLY COMING FROM YOUR TAPS AT {{artwork_address}}?",
      font_size_pt: spec?.headline_defaults?.font_size_pt || 25,
      min_font_size_pt: spec?.headline_defaults?.min_font_size_pt || 13,
      max_lines: spec?.headline_defaults?.max_lines || 4,
      bold: true, align: "left", color: side === "back" ? "#000000" : "#0f172a",
      line_spacing: 1.14, locked: false,
    };
    setElements((p) => [...p, el]);
    setSelected(el.id);
    setDirty(true);
  };

  // MVP: exactly one dynamic QR per side, bound to the existing AWARE
  // campaign/property QR. No arbitrary URLs, no second token system.
  const addQr = () => {
    if (elements.some((e) => e.kind === "qr")) {
      toast.error("This side already has a dynamic QR code.");
      return;
    }
    const el = { ...qrDefaults(side, spec), id: uid() };
    setElements((p) => [...p, el]);
    setSelected(el.id);
    setDirty(true);
  };

  const removeEl = () => {
    if (!sel) return;
    setElements((p) => p.filter((e) => e.id !== sel.id));
    setSelected(null);
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/postcard-studio/versions/${vid}`, {
        front_elements: front, reverse_elements: back,
      });
      setVersion(data.version);
      setFront(data.version.front_elements || []);
      setBack(data.version.reverse_elements || []);
      setPreflight(null);
      setDirty(false);
      toast.success("Template saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const upload = async (file, whichSide) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("fit", "fit");
    try {
      const { data } = await api.post(`/admin/postcard-studio/versions/${vid}/artwork/${whichSide}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${whichSide === "front" ? "Front" : "Reverse"} artwork uploaded`);
      (data.asset.warnings || []).forEach((w) => toast.warning(w));
      setArtworkKey((k) => k + 1);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const setFit = async (whichSide, fit) => {
    setBusy(true);
    try {
      await api.patch(`/admin/postcard-studio/versions/${vid}/artwork/${whichSide}`, { fit });
      await load();
      toast.success(`Artwork set to ${fit}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change fit"); }
    finally { setBusy(false); }
  };

  const runPreflight = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/postcard-studio/versions/${vid}/preflight`);
      setPreflight(data);
      data.ok ? toast.success("Preflight PASS") : toast.error("Preflight FAIL");
    } catch (e) { toast.error(e.response?.data?.detail || "Preflight failed"); }
    finally { setBusy(false); }
  };

  const markReady = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/postcard-studio/versions/${vid}/ready`);
      toast.success("Template version marked Ready");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not mark Ready"); }
    finally { setBusy(false); }
  };

  const newVersion = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/postcard-studio/templates/${template.id}/versions`);
      window.location.assign(`/admin/postcard-studio/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create version"); }
    finally { setBusy(false); }
  };

  /* Proofs always render the template as it is RIGHT NOW in the editor —
     unsaved elements included. The POST preview routes render only; they
     never write the version, assign it or mail anything. */
  const draftBody = () => ({ front_elements: front, reverse_elements: back });
  const previewQuery = (side, fmt) =>
    `/admin/postcard-studio/versions/${vid}/preview?campaign_id=${campaignId}`
    + `&invite_id=${inviteId}&side=${side}&fmt=${fmt}`;

  const loadPreviewMeta = async () => {
    if (!campaignId) return toast.error("Choose a postcard campaign first");
    if (!inviteId) return toast.error("Choose a property first");
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/postcard-studio/versions/${vid}/preview-meta`,
        draftBody(), { params: { campaign_id: campaignId, invite_id: inviteId } });
      setPreviewMeta(data);
      setPreviewError("");
      setPreviewKey((k) => k + 1);
    } catch (e) {
      setPreviewMeta(null);
      setPreviewError(e.response?.data?.detail || e.message || "Preview failed");
    } finally { setBusy(false); }
  };

  const loadProofs = async (randomise) => {
    if (!campaignId) return toast.error("Choose a postcard campaign first");
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/postcard-studio/versions/${vid}/random-proofs`, draftBody(),
        { params: { campaign_id: campaignId, sample: 10,
                    seed: randomise ? Date.now() % 99999 : 0 } });
      setProofs(data);
      setProofsError(data.sample === 0 ? "This campaign has no recipients to preview." : "");
    } catch (e) {
      setProofs(null);
      setProofsError(e.response?.data?.detail || e.message || "Could not build proofs");
    } finally { setBusy(false); }
  };

  const assignToCampaign = async () => {
    if (!campaignId) return toast.error("Choose a campaign first");
    setBusy(true);
    try {
      await api.post(`/admin/property-campaigns/${campaignId}/postcard-template`, {
        template_version_id: vid,
      });
      toast.success("Assigned to campaign");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); }
    finally { setBusy(false); }
  };

  if (!version || !spec) {
    return <div className="max-w-6xl mx-auto px-4 py-8 text-sm text-slate-500" data-testid="ps-editor-loading">Loading…</div>;
  }

  const asset = side === "front" ? version.front_artwork_asset : version.reverse_artwork_asset;
  /* Sep 2026 — a reverse designed as a full postcard back prints edge to
     edge, so the canvas must show it in the same box the renderer uses. */
  const artScope = (asset?.scope === "full_card" && side === "back")
    ? "full_card"
    : (side === "back" && asset?.width_px && asset?.height_px
      && Math.abs(asset.width_px / asset.height_px
        - spec.card_mm[0] / spec.card_mm[1])
       < Math.abs(asset.width_px / asset.height_px
        - spec.back_box_mm[2] / spec.back_box_mm[3])
      ? "full_card" : "panel");
  const isReady = version.status === "ready";

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="ps-editor-page">
      <Link to="/admin/postcard-studio" data-testid="ps-editor-back"
        className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-3.5 h-3.5" /> Postcard Studio
      </Link>
      <div className="mt-1 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="ps-editor-title">
          {template?.name} — V{version.version_number}
        </h1>
        <span data-testid="ps-editor-status" className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${isReady ? "bg-emerald-100 text-emerald-900 border border-emerald-300" : "bg-amber-100 text-amber-900 border border-amber-300"}`}>
          {version.status}
        </span>
        {locked && (
          <span data-testid="ps-editor-locked" className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-rose-100 text-rose-900 border border-rose-300">
            <Lock className="w-3 h-3 inline mb-0.5" /> Locked — LIVE mailed
          </span>
        )}
      </div>

      {locked && (
        <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="ps-locked-note">
          This version has been used in a LIVE mailed campaign, so it is immutable.
          Use <b>Create New Version</b> to make changes — historical campaigns keep
          referencing the exact version they mailed.
          <Button size="sm" variant="outline" className="rounded-full ml-2" data-testid="ps-locked-new-version" onClick={newVersion} disabled={busy}>
            <GitBranch className="w-3.5 h-3.5 mr-1" /> Create New Version
          </Button>
        </div>
      )}

      {/* toolbar */}
      <div className={`${card} mt-4 flex flex-wrap items-center gap-2`} data-testid="ps-toolbar">
        <div className="inline-flex rounded-full border border-slate-300 overflow-hidden" data-testid="ps-side-tabs">
          {["front", "back"].map((s) => (
            <button key={s} data-testid={`ps-tab-${s}`} onClick={() => { setSide(s); setSelected(null); }}
              className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider ${side === s ? "bg-cyan-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              {s === "front" ? "Front" : "Back"}
            </button>
          ))}
        </div>
        <Button size="sm" variant="outline" className="rounded-full" data-testid="ps-add-text" onClick={addText} disabled={locked}>
          <Type className="w-3.5 h-3.5 mr-1" /> Add Text Block
        </Button>
        <Button size="sm" variant="outline" className="rounded-full" data-testid="ps-add-qr" onClick={addQr} disabled={locked}>
          <QrCode className="w-3.5 h-3.5 mr-1" /> Add QR Code
        </Button>
        <input ref={frontFile} type="file" accept=".png,.jpg,.jpeg,.pdf" className="hidden"
          data-testid="ps-upload-front-input" onChange={(e) => upload(e.target.files?.[0], "front")} />
        <input ref={backFile} type="file" accept=".png,.jpg,.jpeg,.pdf" className="hidden"
          data-testid="ps-upload-back-input" onChange={(e) => upload(e.target.files?.[0], "back")} />
        <span className="w-full text-[11px] text-slate-500" data-testid="ps-artwork-guidance">
          For dynamic QR campaigns, upload artwork with the QR area left blank —
          the recipient&apos;s QR is overlaid at render time. Keep &quot;SCAN
          HERE&quot; style copy in the artwork or a separate text layer.
        </span>
        <Button size="sm" variant="outline" className="rounded-full" data-testid="ps-upload-front"
          onClick={() => frontFile.current?.click()} disabled={busy || locked}>
          <Upload className="w-3.5 h-3.5 mr-1" /> Upload Front Artwork
        </Button>
        <Button size="sm" variant="outline" className="rounded-full" data-testid="ps-upload-back"
          onClick={() => backFile.current?.click()} disabled={busy || locked}>
          <Upload className="w-3.5 h-3.5 mr-1" /> Upload Reverse Artwork
        </Button>
        <span className="ml-auto flex flex-wrap gap-2">
          <Button size="sm" className="rounded-full bg-cyan-700 hover:bg-cyan-800" data-testid="ps-save" onClick={save} disabled={busy || locked || !dirty}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} {dirty ? "Save" : "Saved"}
          </Button>
          <Button size="sm" variant="outline" className="rounded-full" data-testid="ps-preflight" onClick={runPreflight} disabled={busy}>
            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Run Preflight
          </Button>
          <Button size="sm" className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="ps-mark-ready" onClick={markReady} disabled={busy || locked || dirty}>
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark Ready
          </Button>
        </span>
      </div>

      {side === "back" && (
        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid="ps-greyscale-note">
          {spec.reverse_note}{" "}
          {artScope === "full_card" ? (
            <>
              This reverse is a <b>full-card design ({spec.card_mm[0]} × {spec.card_mm[1]} mm)</b>,
              so it prints edge to edge at full size and the hatched
              <b> BING POSTAL AREA — LOCKED</b> strip is masked white on every card —
              keep all copy, logos and the QR inside the left {spec.postal_zone_x_mm} mm.
            </>
          ) : (
            <>
              Our artwork must stay inside the left {spec.back_box_mm[2]} × {spec.back_box_mm[3]} mm —
              the hatched strip is <b>BING POSTAL AREA — LOCKED</b>. Upload a full
              {" "}{spec.card_mm[0]} × {spec.card_mm[1]} mm design instead and it will print edge to edge.
            </>
          )}
          <button className="ml-1 underline font-semibold text-cyan-800 hover:text-cyan-900"
            data-testid="ps-reverse-guide"
            onClick={() => openAuthedPdf("/admin/postcard-studio/reverse-template.pdf")
              .catch((e) => toast.error(e.message))}>
            Download the reverse design guide (PDF)
          </button>
        </div>
      )}

      <div className="mt-4 grid lg:grid-cols-[minmax(0,1fr)_300px] gap-4">
        <div className={card}>
          <PostcardCanvas side={side} versionId={vid} elements={elements} selected={selected}
            onSelect={setSelected} onChange={updateEl} spec={spec} scale={4}
            locked={locked} artworkKey={artworkKey} artScope={artScope} />
          <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
            <span>Card {spec.card_mm[0]} × {spec.card_mm[1]} mm · {spec.reserved_edge_mm} mm reserved edge</span>
            {asset && (
              <>
                <span data-testid="ps-asset-info">{asset.filename} · {asset.width_px}×{asset.height_px}px</span>
                <span className="inline-flex items-center gap-1">
                  {spec.fit_modes.map((m) => (
                    <button key={m} data-testid={`ps-fit-${m}`} onClick={() => setFit(side, m)} disabled={locked}
                      className={`rounded-full px-2 py-0.5 font-bold uppercase tracking-wider border ${asset.fit === m ? "bg-cyan-700 text-white border-cyan-700" : "bg-white border-slate-300 text-slate-600"}`}>
                      {m}
                    </button>
                  ))}
                </span>
              </>
            )}
            {(asset?.warnings || []).map((w, i) => (
              <span key={i} className="text-amber-700 font-semibold" data-testid={`ps-art-warning-${i}`}>{w}</span>
            ))}
          </div>
        </div>

        {/* inspector */}
        <div className={card} data-testid="ps-inspector">
          {!sel && (
            <div className="text-xs text-slate-500">
              Select an element to edit it, or add a text block / QR code.
              <div className="mt-3">
                <div className={label}>Merge fields</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {spec.merge_fields.map((f) => (
                    <code key={f.key} title={f.description}
                      className="text-[10px] bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-slate-700">
                      {f.token}
                    </code>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-slate-500">
                  <b>{"{{artwork_address}}"}</b> renders as street address + suburb only
                  (e.g. 23 Sunrise Road, Doonan). Owner names are optional — a line that
                  resolves to nothing is hidden automatically.
                </p>
              </div>
            </div>
          )}
          {sel && (
            <div className="space-y-2" data-testid={`ps-inspector-${sel.kind}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                  {sel.kind === "qr" ? "QR code" : "Text block"}
                </span>
                <span className="flex gap-1">
                  <button data-testid="ps-el-lock" title={sel.locked ? "Unlock" : "Lock"}
                    onClick={() => { updateEl({ ...sel, locked: !sel.locked }); }}
                    className="p-1 rounded hover:bg-slate-100">
                    {sel.locked ? <Lock className="w-3.5 h-3.5 text-rose-600" /> : <Unlock className="w-3.5 h-3.5 text-slate-500" />}
                  </button>
                  <button data-testid="ps-el-delete" title="Delete" onClick={removeEl} disabled={locked}
                    className="p-1 rounded hover:bg-rose-50">
                    <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                  </button>
                </span>
              </div>

              {sel.kind === "qr" && (
                <div className="space-y-2" data-testid="ps-qr-inspector">
                  <div className="rounded-lg border border-cyan-200 bg-cyan-50/70 p-2">
                    <div className={label}>QR source</div>
                    <div className="text-sm font-bold text-cyan-900" data-testid="ps-qr-source">
                      Property / Campaign QR
                    </div>
                    <p className="mt-1 text-[11px] text-cyan-900/80">
                      Resolved per recipient from the existing AWARE property QR
                      token at generation time. Fixed in this release — no
                      arbitrary URLs and no second QR system.
                    </p>
                  </div>
                  <div className="text-[11px] text-slate-600" data-testid="ps-qr-printed-size">
                    Printed size <b>{sel.w_mm} × {sel.h_mm} mm</b> (square,
                    minimum {spec?.min_qr_mm || 25} mm, plus a
                    {" "}{spec?.qr_quiet_zone_mm ?? 2} mm quiet zone)
                  </div>
                  <div>
                    <div className={label}>Snap to a safe corner</div>
                    <div className="mt-1 grid grid-cols-2 gap-1.5">
                      {QR_PRESETS.map((pr) => (
                        <Button key={pr.key} size="sm" variant="outline"
                          className="rounded-lg text-[11px] h-7"
                          data-testid={`ps-qr-preset-${pr.key}`} disabled={locked || sel.locked}
                          onClick={() => updateEl({ ...sel, ...presetPos(pr.key, side, spec, sel) })}>
                          {pr.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-full w-full"
                    data-testid="ps-qr-copy-side" onClick={copyQrToOtherSide} disabled={locked}>
                    <Copy className="w-3.5 h-3.5 mr-1" />
                    Copy QR to {side === "front" ? "Back" : "Front"}
                  </Button>
                  <p className="text-[11px] text-slate-500">
                    Drag, or nudge with the arrow keys (Shift = 5 mm). The QR is
                    clamped to the printable area and can never enter the Bing
                    postal strip.
                  </p>
                </div>
              )}

              {sel.kind === "text" && (
                <>
                  <label className="block">
                    <span className={label}>Text (merge fields allowed)</span>
                    <textarea data-testid="ps-el-text" rows={4} value={sel.text}
                      onChange={(e) => updateEl({ ...sel, text: e.target.value })}
                      className={`${input} font-mono`} disabled={locked || sel.locked} />
                  </label>
                  <div className="flex flex-wrap gap-1">
                    {spec.merge_fields.map((f) => (
                      <button key={f.key} data-testid={`ps-insert-${f.key}`} disabled={locked || sel.locked}
                        onClick={() => updateEl({ ...sel, text: `${sel.text}${f.token}` })}
                        className="text-[10px] rounded border border-slate-300 bg-white px-1.5 py-0.5 hover:bg-slate-50 text-slate-700">
                        {f.token}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <label><span className={label}>Font size (pt)</span>
                      <input data-testid="ps-el-font" type="number" step="0.5" value={sel.font_size_pt} className={input}
                        onChange={(e) => updateEl({ ...sel, font_size_pt: Number(e.target.value) })} disabled={locked || sel.locked} /></label>
                    <label><span className={label}>Min size (pt)</span>
                      <input data-testid="ps-el-min-font" type="number" step="0.5" value={sel.min_font_size_pt} className={input}
                        onChange={(e) => updateEl({ ...sel, min_font_size_pt: Number(e.target.value) })} disabled={locked || sel.locked} /></label>
                    <label><span className={label}>Max lines</span>
                      <input data-testid="ps-el-max-lines" type="number" value={sel.max_lines} className={input}
                        onChange={(e) => updateEl({ ...sel, max_lines: Number(e.target.value) })} disabled={locked || sel.locked} /></label>
                    <label><span className={label}>Line spacing</span>
                      <input data-testid="ps-el-line-spacing" type="number" step="0.02" value={sel.line_spacing} className={input}
                        onChange={(e) => updateEl({ ...sel, line_spacing: Number(e.target.value) })} disabled={locked || sel.locked} /></label>
                    <label><span className={label}>Alignment</span>
                      <select data-testid="ps-el-align" value={sel.align} className={input}
                        onChange={(e) => updateEl({ ...sel, align: e.target.value })} disabled={locked || sel.locked}>
                        <option value="left">Left</option><option value="center">Centre</option><option value="right">Right</option>
                      </select></label>
                    <label><span className={label}>Text colour</span>
                      <input data-testid="ps-el-color" type="color" value={sel.color} className={`${input} h-8 p-0.5`}
                        onChange={(e) => updateEl({ ...sel, color: e.target.value })} disabled={locked || sel.locked} /></label>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                    <input data-testid="ps-el-bold" type="checkbox" checked={!!sel.bold}
                      onChange={(e) => updateEl({ ...sel, bold: e.target.checked })} disabled={locked || sel.locked} />
                    Bold
                  </label>
                </>
              )}

              {sel.kind === "qr" && (
                <p className="text-[11px] text-slate-600" data-testid="ps-qr-note">
                  Uses this property's existing AWARE QR token — one QR system, no second
                  token. Minimum printed size {spec.min_qr_mm} mm with a {spec.qr_quiet_mm} mm
                  clear quiet zone. Aspect ratio stays square.
                </p>
              )}

              <div className="grid grid-cols-4 gap-1.5">
                {["x_mm", "y_mm", "w_mm", "h_mm"].map((k) => (
                  <label key={k}><span className={label}>{k.replace("_mm", "").toUpperCase()}</span>
                    <input data-testid={`ps-el-${k}`} type="number" step="0.5" value={sel[k]} className={input}
                      disabled={locked || sel.locked}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        const next = { ...sel, [k]: v };
                        if (sel.kind === "qr" && (k === "w_mm" || k === "h_mm")) { next.w_mm = v; next.h_mm = v; }
                        updateEl(next);
                      }} /></label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* preflight */}
      {preflight && (
        <div className={`${card} mt-4 border-2 ${preflight.ok ? "border-emerald-300" : "border-rose-300"}`} data-testid="ps-preflight-panel">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-bold ${preflight.ok ? "text-emerald-700" : "text-rose-700"}`} data-testid="ps-preflight-result">
              Preflight {preflight.result}
            </span>
            <span className="text-[11px] text-slate-500">{new Date(preflight.checked_at).toLocaleString()}</span>
          </div>
          {preflight.problems?.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-xs text-rose-700 space-y-0.5" data-testid="ps-preflight-problems">
              {preflight.problems.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          )}
          {preflight.warnings?.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-xs text-amber-700 space-y-0.5" data-testid="ps-preflight-warnings">
              {preflight.warnings.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* recipient previews + assignment */}
      <div className={`${card} mt-4`} data-testid="ps-preview-panel">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-700">Preview real recipients</span>
          <select data-testid="ps-campaign-select" value={campaignId} onChange={(e) => setCampaignId(e.target.value)}
            disabled={campaigns.length === 0}
            className="w-full sm:w-auto rounded-lg border border-slate-300 px-2 py-1.5 text-xs disabled:bg-slate-100">
            <option value="">
              {campaigns.length === 0 ? "No postcard campaigns available" : "Choose a postcard campaign…"}
            </option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.code}) · {c.status} · {c.recipients} {c.recipients === 1 ? "property" : "properties"}
              </option>
            ))}
          </select>
          <select data-testid="ps-recipient-select" value={inviteId} onChange={(e) => setInviteId(e.target.value)}
            disabled={!campaignId || recipients.length === 0}
            className="w-full sm:w-auto rounded-lg border border-slate-300 px-2 py-1.5 text-xs sm:max-w-[280px] disabled:bg-slate-100">
            <option value="">
              {!campaignId ? "Choose a campaign first…"
                : recipientsState === "loading" ? "Loading properties…"
                  : recipients.length === 0 ? "No properties in this campaign"
                    : "Choose a property…"}
            </option>
            {recipients.map((r) => <option key={r.id} value={r.id}>{r.street_address}, {r.suburb}</option>)}
          </select>
          <Button size="sm" variant="outline" className="rounded-full" data-testid="ps-preview-recipient"
            onClick={loadPreviewMeta} disabled={busy || !campaignId || !inviteId}>
            <Eye className="w-3.5 h-3.5 mr-1" /> Preview Recipient
          </Button>
          <Button size="sm" variant="outline" className="rounded-full" data-testid="ps-preview-pdf"
            disabled={busy || !previewMeta}
            onClick={() => openAuthedPdf(previewQuery("both", "pdf"), draftBody())
              .catch((e) => toast.error(e.message))}>
            Open proof PDF
          </Button>
          <Button size="sm" variant="outline" className="rounded-full" data-testid="ps-preview-random"
            onClick={() => loadProofs(false)} disabled={busy || !campaignId}>
            <Shuffle className="w-3.5 h-3.5 mr-1" /> Preview Random 10
          </Button>
          {proofs && (
            <Button size="sm" variant="outline" className="rounded-full" data-testid="ps-refresh-random"
              onClick={() => loadProofs(true)} disabled={busy}>
              Refresh Random 10
            </Button>
          )}
          <Button size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800 ml-auto" data-testid="ps-assign-campaign"
            onClick={assignToCampaign} disabled={busy || !campaignId || !isReady}>
            <Link2 className="w-3.5 h-3.5 mr-1" /> Assign to Campaign
          </Button>
        </div>

        <p className="mt-2 text-[11px] text-slate-500" data-testid="ps-preview-explainer">
          Proofs render your current editor state — unsaved edits included. Nothing is
          saved, assigned or mailed by previewing.
        </p>

        {campaignsError && (
          <p className="mt-2 text-[11px] font-semibold text-rose-700" data-testid="ps-campaigns-error">
            {campaignsError}
          </p>
        )}
        {campaignMeta && !campaignsError && campaigns.length === 0 && (
          <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="ps-no-campaigns">
            <p className="text-[11px] font-bold text-amber-900">
              No postcard campaigns yet — {campaignMeta.total_campaigns} campaigns exist,
              all of them A4 letter campaigns. Switch one to Postcard to preview real
              recipients (Draft and Test campaigns are fine).
            </p>
            <div className="mt-2 space-y-1">
              {(campaignMeta.other_campaigns || []).map((c) => (
                <div key={c.id} className="flex flex-wrap items-center gap-2 text-[11px] text-slate-700"
                  data-testid={`ps-convert-row-${c.code}`}>
                  <span className="font-semibold">{c.name} ({c.code})</span>
                  <span className="text-slate-500">
                    {c.status} · {c.recipients} {c.recipients === 1 ? "property" : "properties"}
                  </span>
                  {c.can_convert ? (
                    <Button size="sm" variant="outline" className="rounded-full h-6 px-2 text-[10px]"
                      data-testid={`ps-convert-${c.code}`} disabled={busy}
                      onClick={() => convertToPostcard(c)}>
                      Use as postcard campaign
                    </Button>
                  ) : (
                    <span className="text-rose-700 font-semibold">{c.blocked_reason}</span>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-slate-600">
              Or start a fresh one in{" "}
              <Link to="/admin/property-campaigns" className="underline font-semibold">
                Property campaigns
              </Link>{" "}
              and set its mail format to <b>Postcard</b>.
            </p>
          </div>
        )}
        {campaignId && recipientsState === "loaded" && recipients.length === 0 && (
          <p className="mt-2 text-[11px] text-amber-800 font-semibold" data-testid="ps-no-recipients">
            This campaign has no properties yet — add or import recipients on the campaign,
            then come back to preview.
          </p>
        )}
        {!["idle", "loading", "loaded"].includes(recipientsState) && (
          <p className="mt-2 text-[11px] font-semibold text-rose-700" data-testid="ps-recipients-error">
            {recipientsState}
          </p>
        )}
        {previewError && (
          <p className="mt-2 text-[11px] font-semibold text-rose-700" data-testid="ps-preview-error">
            {previewError}
          </p>
        )}
        {proofsError && (
          <p className="mt-2 text-[11px] font-semibold text-rose-700" data-testid="ps-proofs-error">
            {proofsError}
          </p>
        )}
        {campaignId && inviteId && !previewMeta && !previewError && (
          <p className="mt-2 text-[11px] text-slate-600" data-testid="ps-preview-hint">
            Press <b>Preview Recipient</b> to render this property — merge fields and its own
            QR code resolve, and <b>Open proof PDF</b> unlocks once the proof renders.
          </p>
        )}
        {!isReady && (
          <p className="mt-2 text-[11px] text-amber-700 font-semibold" data-testid="ps-assign-hint">
            Only a Ready version can be assigned to a campaign — run Preflight then Mark
            Ready. Previews work on a Draft version regardless.
          </p>
        )}
        {assigned.length > 0 && (
          <p className="mt-2 text-[11px] text-slate-600" data-testid="ps-assigned-campaigns">
            Assigned to: {assigned.map((c) => `${c.name} (${c.code})`).join(", ")}
          </p>
        )}

        {previewMeta && (
          <div className="mt-3 grid md:grid-cols-2 gap-3">
            <div>
              <div className={label}>Front — full rendered postcard</div>
              <AuthImage testId="ps-preview-front" refreshKey={`${inviteId}-${artworkKey}-${previewKey}`}
                body={draftBody()}
                path={previewQuery("front", "png")}
                alt="Front proof" className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50" />
            </div>
            <div>
              <div className={label}>Reverse — AWARE artwork only (black print)</div>
              <AuthImage testId="ps-preview-back" refreshKey={`${inviteId}-${artworkKey}-${previewKey}`}
                body={draftBody()}
                path={previewQuery("back", "png")}
                alt="Reverse proof" className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50" />
            </div>
          </div>
        )}

        {previewMeta && (
          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="ps-postal-preview">
            <div className={label}>Postal preview</div>
            <div className="mt-1 text-sm font-semibold text-slate-900 leading-snug whitespace-pre-line" data-testid="ps-postal-lines">
              {(previewMeta.postal_lines || []).join("\n")}
            </div>
            <p className="mt-1 text-[11px] text-slate-500">{previewMeta.postal_note}</p>
            {(previewMeta.warnings || []).length > 0 && (
              <ul className="mt-2 list-disc pl-5 text-[11px] text-amber-800 space-y-0.5" data-testid="ps-preview-warnings">
                {previewMeta.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
            {previewMeta.merge_values && (
              <div className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-slate-600" data-testid="ps-merge-values">
                {Object.entries(previewMeta.merge_values).map(([k, v]) => (
                  <div key={k}><code className="text-slate-500">{`{{${k}}}`}</code> → <b>{v || "—"}</b></div>
                ))}
              </div>
            )}
            {previewMeta.qr && (
              <div className="mt-2 rounded-lg border border-slate-200 bg-white p-2 text-[11px] text-slate-700"
                data-testid="ps-preview-qr-meta">
                <b>Dynamic QR</b> · source {previewMeta.qr.source}
                {" · "}reference <code>{previewMeta.qr.reference || "—"}</code>
                {" · "}template {previewMeta.template_version}
                {" · sides: "}
                <b>{[previewMeta.qr.front_qr_enabled && "front",
                     previewMeta.qr.back_qr_enabled && "back"]
                    .filter(Boolean).join(" + ") || "none"}</b>
                {!previewMeta.qr.reference && (
                  <span className="ml-1 font-semibold text-rose-700">
                    — this property has no QR token, so it will be flagged
                    Artwork Requires Review
                  </span>
                )}
              </div>
            )}
            {previewMeta.requires_review && (
              <p className="mt-1 text-[11px] font-semibold text-rose-700">{previewMeta.requires_review}</p>
            )}
          </div>
        )}

        {proofs && (
          <div className="mt-4" data-testid="ps-proofs-grid">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-700">
              Random proofs — {proofs.sample} of {proofs.pool_size}
            </div>
            <p className="text-[11px] text-slate-500">{proofs.postal_note}</p>
            <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {proofs.proofs.map((p) => (
                <div key={p.invite_id} className={`rounded-lg border bg-white p-2 ${p.validation === "OK" ? "border-slate-200" : "border-rose-300"}`}
                  data-testid={`ps-proof-${p.invite_id}`}>
                  <AuthImage testId={`ps-proof-thumb-${p.invite_id}`}
                    refreshKey={`${previewKey}-${p.invite_id}`}
                    body={draftBody()}
                    path={`/admin/postcard-studio/versions/${vid}/preview?campaign_id=${campaignId}&invite_id=${p.invite_id}&side=front&fmt=png`}
                    alt="proof" className="w-full rounded border border-slate-200 bg-slate-50" />
                  <div className="mt-1 text-[11px] font-bold text-slate-900">{p.property_address}</div>
                  <div className="text-[10px] text-slate-500">{p.artwork_address || "—"}</div>
                  <div className={`mt-0.5 text-[10px] font-semibold ${p.validation === "OK" ? "text-emerald-700" : "text-rose-700"}`}>
                    {p.validation}{p.problems?.length ? ` — ${p.problems[0]}` : ""}
                  </div>
                  <button className="mt-1 text-[11px] font-semibold text-cyan-700 hover:text-cyan-900"
                    data-testid={`ps-proof-open-${p.invite_id}`}
                    onClick={() => openAuthedPdf(`/admin/postcard-studio/versions/${vid}/preview?campaign_id=${campaignId}&invite_id=${p.invite_id}&side=both&fmt=pdf`, draftBody()).catch((e) => toast.error(e.message))}>
                    Open full size
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


/** Safe default placement for a dynamic QR on a given side. The reverse
    default stays well inside the 116 mm AWARE content area so a copied
    QR can never land in the Bing postal strip. */
function qrDefaults(side, spec) {
  const size = (spec?.min_qr_mm || 25) + 5;
  const quiet = spec?.qr_quiet_zone_mm ?? 2;
  const box = side === "front" ? (spec?.front_box_mm || [2, 2, 206, 126])
                               : (spec?.back_box_mm || [0, 2, 116, 126]);
  const right = side === "back"
    ? Math.min(box[0] + box[2], spec?.postal_zone_x_mm ?? 116) : box[0] + box[2];
  return {
    kind: "qr", source: "campaign_property_qr", locked: false,
    w_mm: size, h_mm: size,
    x_mm: Math.round((right - quiet - size - 4) * 10) / 10,
    y_mm: Math.round((box[1] + box[3] - quiet - size - 4) * 10) / 10,
  };
}


/** One-click QR placements. Every preset lands fully inside the printable
    area with its quiet zone, and on the reverse it is additionally kept
    clear of the Bing postal strip. */
const QR_PRESETS = [
  { key: "bottom-right", label: "Bottom right" },
  { key: "bottom-left", label: "Bottom left" },
  { key: "top-right", label: "Top right" },
  { key: "top-left", label: "Top left" },
];

function presetPos(key, side, spec, el) {
  const quiet = spec?.qr_quiet_zone_mm ?? 2;
  const pad = 4;
  const box = side === "front" ? (spec?.front_box_mm || [2, 2, 206, 126])
                               : (spec?.back_box_mm || [0, 2, 116, 126]);
  const right = side === "back"
    ? Math.min(box[0] + box[2], spec?.postal_zone_x_mm ?? 116) : box[0] + box[2];
  const minX = box[0] + quiet + pad;
  const minY = box[1] + quiet + pad;
  const maxX = right - quiet - pad - el.w_mm;
  const maxY = box[1] + box[3] - quiet - pad - el.h_mm;
  const r = (v) => Math.round(Math.max(minX === v ? minX : v, 0) * 10) / 10;
  const pos = {
    "bottom-right": [maxX, maxY], "bottom-left": [minX, maxY],
    "top-right": [maxX, minY], "top-left": [minX, minY],
  }[key];
  return { x_mm: r(Math.max(minX, Math.min(pos[0], maxX))),
           y_mm: r(Math.max(minY, Math.min(pos[1], maxY))) };
}
